import PropTypes from 'prop-types';
import Flux from '../../../flux/Flux.js';
import { forwardRef, useEffect, useState } from 'react';

/**
 * HOC that tracks first-time video plays and sends a view notification to the backend
 * @param {Component} WrappedComponent - The component to wrap (VideoPlayer, VXShaka, etc.)
 * @returns {Component} - Enhanced component with tracking capability
 */
const withAlbumView = (WrappedComponent) => {
	const WithAlbumView = (props, ref) => {
		// Track which videos have already been played during this session
		const [playedVideos, setPlayedVideos] = useState(new Set());

		// Clear played videos on unmount to avoid memory leaks
		useEffect(() => () => setPlayedVideos(new Set()), []);

		/**
		 * Handles the first play of a video and sends request to backend
		 */
		const handleFirstPlay = () => {
			// Call original onFirstPlay if provided
			if (props.onPlay) {
				props.onPlay();
			}

			const albumId = props.albumId;
			if (!albumId) {
				return;
			}

			// Check if this video has already been played
			if (!playedVideos.has(albumId)) {
				// Add to played videos set
				setPlayedVideos((prevPlayedVideos) => {
					const newPlayedVideos = new Set(prevPlayedVideos);
					newPlayedVideos.add(albumId);
					return newPlayedVideos;
				});

				// Send view notification to backend - not for previews
				if (!props.showPreviewLabel) {
					Flux.Guest.addAlbumView(albumId);
				}
			}
		};

		// Pass the handleFirstPlay function to the wrapped component
		return <WrappedComponent {...props} ref={ref} onPlay={handleFirstPlay} />;
	};

	// Set display name for debugging
	WithAlbumView.displayName = `WithAlbumView(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

	// Add prop types validation
	WithAlbumView.propTypes = {
		albumId: PropTypes.string.isRequired,
		onPlay: PropTypes.func,
		showPreviewLabel: PropTypes.bool,
	};

	return forwardRef(WithAlbumView);
};

export default withAlbumView;
